import { createFeatureSelector, createSelector } from "@ngrx/store";
import { AppState } from "@toro/common/shared/interfaces/app-state.interface";
import { FinanceState } from "@toro/common/shared/state-management/finance/finance.reducer";
import { ICheckoutStock } from "../../interfaces/checkout.interface";
import { calculateInvestment } from "../panels/checkout/utils/investment-calculation";

/**
 * Seleciona a fatia (feature state) 'finance' do estado global da aplicação.
 *
 * @returns {FinanceState} O objeto completo do estado financeiro.
 * @example
 * ```typescript
 * // Em um efeito ou serviço, para obter todo o estado financeiro.
 * this.store.select(selectFinanceState).subscribe(financeState => {
 *  console.log(financeState.finance, financeState.consolidatePosition);
 * });
 * ```
 */
export const selectFinanceState = createFeatureSelector<AppState, FinanceState>('finance');

/**
 * Seleciona a propriedade `finance` de dentro do estado `FinanceState`.
 * Contém informações sobre saldos e valores projetados.
 *
 * @returns {FinancePosition} O objeto com a posição financeira do usuário.
 * @example
 * ```typescript
 * // Em um componente, para exibir o saldo em conta.
 * this.financeData$ = this.store.select(selectFinancePosition);
 * ```
 */
export const selectFinancePosition = createSelector(
  selectFinanceState,
  (state) => state.finance
);

/**
 * Seleciona a propriedade `consolidatePosition` de dentro do estado `FinanceState`.
 * Contém informações sobre garantias e patrimônio total.
 *
 * @returns {ConsolidatedPosition} O objeto com a posição consolidada do usuário.
 * @example
 * ```typescript
 * // Em um componente, para exibir a garantia total.
 * this.consolidatedPosition$ = this.store.select(selectConsolidatedPosition);
 * ```
 */
export const selectConsolidatedPosition = createSelector(
  selectFinanceState,
  (state) => state.consolidatePosition
);

/**
 * Retorna uma factory function que cria um seletor para o saldo disponível para investir.
 * A lógica varia se o ativo (`stock`) é do mercado futuro ou de ações.
 *
 * @param stock O objeto de estoque específico para o qual o cálculo será feito.
 * @returns Um seletor memoizado que extrai o valor do saldo disponível do estado.
 * @example
 * ```typescript
 * // Em um componente, ao preparar um investimento.
 * const myStock: ICheckoutStock = { stockId: 'PETR4', isFuture: false, ... };
 *
 * this.availableToInvest$ = this.store.select(
 *  selectAvailableToInvest(myStock)
 * );
 * ```
 */
export const selectAvailableToInvest = (stock: ICheckoutStock) =>
  createSelector(
    selectFinancePosition,
    (finance) => {
      if (!finance || !stock) return 0;
      return stock.isFuture ? finance.balanceUsedToValidateFuture : finance.balanceUsedToValidateStock;
    }
  );

 /**
 * Retorna uma factory function que cria um seletor para a garantia disponível, garantindo que o valor nunca seja negativo.
 *
 * @param stock O objeto de estoque específico para o qual o cálculo será feito.
 * @returns Um seletor memoizado que calcula a garantia operacional disponível.
 * @example
 * ```typescript
 * // Em um componente, para verificar a garantia antes de uma operação.
 * const myStock: ICheckoutStock = { stockId: 'WDOV25', isFuture: true, ... };
 *
 * this.availableGuarantee$ = this.store.select(
 *  selectAvailableGuarantee(myStock)
 * );
 * ```
 */
export const selectAvailableGuarantee = (stock: ICheckoutStock) =>
  createSelector(
    selectConsolidatedPosition,
    selectAvailableToInvest(stock),
    (consolidatedPosition, availableToInvest) => {
      if (!consolidatedPosition) return 0;
      const guaranteeValue = consolidatedPosition.totalOperationGuarantee - availableToInvest;
      return guaranteeValue < 0 ? 0 : guaranteeValue;
    }
  );

 /**
 * Retorna uma factory function que cria um seletor para o valor financeiro total do investimento.
 *
 * @param stock O objeto de estoque contendo quantidade, preço, etc.
 * @returns Um seletor memoizado que calcula o valor total do investimento.
 * @example
 * ```typescript
 * // Em um componente, para exibir o custo total de uma ordem.
 * const myStock: ICheckoutStock = { stockId: 'MGLU3', unitPrice: 2.50, buyAmount: 100, ... };
 *
 * this.totalInvestmentCost$ = this.store.select(
 *  selectInvestments(myStock)
 * ); // Irá emitir 250
 * ```
 */
export const selectInvestments = (stock: ICheckoutStock) =>
  createSelector(
    () => stock,
    (currentStock) => {
        if (!currentStock) return 0;
        return calculateInvestment(currentStock);
    }
  );

 /**
 * Retorna uma factory function que cria um seletor para o saldo/garantia restante após o investimento.
 *
 * @param stock O objeto de estoque específico para o qual o cálculo será feito.
 * @returns Um seletor memoizado que calcula o valor disponível restante após a operação.
 * @example
 * ```typescript
 * // Em um componente, para mostrar ao usuário quanto ele terá de saldo/garantia após investir.
 * const myStock: ICheckoutStock = { stockId: 'VALE3', unitPrice: 60, buyAmount: 10, ... };
 *
 * this.availableAfter$ = this.store.select(
 *  selectAvailableAfterInvestment(myStock)
 * );
 * ```
 */
export const selectAvailableAfterInvestment = (stock: ICheckoutStock) =>
  createSelector(
    selectConsolidatedPosition,
    selectAvailableToInvest(stock),
    selectAvailableGuarantee(stock),
    selectInvestments(stock),
    (consolidatedPosition, availableToInvest, availableGuarantee, investments) => {
      if (!consolidatedPosition) return 0;
      if (consolidatedPosition.totalOperationGuarantee > 0) {
        return availableGuarantee - investments;
      } else {
        return availableToInvest - investments;
      }
    }
  );
